export class Engine {
    constructor(boardApi) {
      this.boardApi = boardApi;

      this.currentEvaluation = undefined;

      this.bestMove = null;

      this.resolveEval = null;
  

      const wasmSupported =
        typeof WebAssembly === 'object' &&
        WebAssembly.validate(Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00));
  

      this.stockfish = new Worker(wasmSupported ? 'stockfish.wasm.js' : 'stockfish.js');
  
      this.setupListeners();
      this.stockfish.postMessage('uci');
    }
  
    setupListeners() {
      this.stockfish.addEventListener('message', (event) => this.handleEngineOutput(event));
      this.stockfish.addEventListener('error', (err) => console.error('Stockfish Error:', err));
      this.stockfish.addEventListener('messageerror', (err) => console.error('Message Error:', err));
    }
  
    handleEngineOutput(event) {
      const message = event.data;
  
      const parts = message.trim().split(/\s+/);
  
      // The standard UCI init messages
      if (parts[0] === 'uciok') {
        // Set engine options (example: analyze mode, contempt, etc.)
        this.setOption('UCI_AnalyseMode', 'true');
        this.setOption('Analysis Contempt', 'Off');
        this.stockfish.postMessage('ucinewgame');
        this.stockfish.postMessage('isready');
        return;
      }
  
      if (parts[0] === 'readyok') {
        // The engine has acknowledged it's ready
        return;
      }
  

      if (message.startsWith('info') && message.includes('score')) {
        // parse out the cp or mate value
        const cpIndex = parts.indexOf('cp');
        const mateIndex = parts.indexOf('mate');
  
        if (cpIndex !== -1) {
          // We have a centipawn score
          const cpVal = parseInt(parts[cpIndex + 1], 10);
          this.currentEvaluation = cpVal;
        } else if (mateIndex !== -1) {
          // We have a mate score
          // e.g. "score mate 2" means mate in 2 (from your side’s perspective).
          // "score mate -1" typically means you’re 1 move away from being mated.
          const mateVal = parseInt(parts[mateIndex + 1], 10);
  
          // You can decide how to treat mate scores numerically:
          // Positive if you're delivering mate, negative if you're getting mated
          // For instance:
          if (mateVal < 0) {
            this.currentEvaluation = -999; // losing position
          } else {
            this.currentEvaluation = 999; // winning position
          }
        }
      }
  
      // The final line after analysis is done
      if (parts[0] === 'bestmove') {
        this.bestMove = parts[1] || null;
  
        // If we have an outstanding promise waiting for final evaluation, resolve it:
        if (this.resolveEval) {
          this.resolveEval(this.currentEvaluation);
          this.resolveEval = null; // reset
        }
      }
    }
  
    setOption(name, value) {
      this.stockfish.postMessage(`setoption name ${name} value ${value}`);
    }
  
    /**
     * Evaluate a position at `fen` and return a Promise
     * that resolves once we have a final evaluation (on 'bestmove').
     */
    evaluatePosition(fen) {
      // Reset the current evaluation
      this.currentEvaluation = undefined;
  
      // If a previous evaluation promise existed, clear it
      if (this.resolveEval) {
        this.resolveEval(undefined);
        this.resolveEval = null;
      }
  
      // Create a fresh Promise we can await in handleMove()
      const evaluationPromise = new Promise((resolve) => {
        this.resolveEval = (finalEval) => {
          resolve(finalEval);
        };
      });
  
      // Instruct engine to evaluate from this position
      // Note: you might want to "ucinewgame" and "isready" first if needed
      this.stockfish.postMessage(`position fen ${fen}`);
      // 1 second analysis time
      this.stockfish.postMessage('go movetime 1000');
  
      return evaluationPromise;
    }
  }
  